// Strings for the head of school's own screens (Workflow H, 0015): home,
// approvals as primary or delegate, deputies, the school's Activity log, and
// two-factor wording for school roles. Merged into STRINGS in strings.ts, so
// every language must define every key (checked by the type below). Finnish
// and Swedish need native-speaker review before go-live, like the rest.

type Shape<T> = { [K in keyof T]: T[K] extends (...a: infer A) => string ? (...a: A) => string : string };

const n = (count: number, one: string, many: string) => (count === 1 ? one : many);

export const headEn = {
  // ── navigation and page ──
  'hs.navOverview': 'Overview',
  'hs.navResults': 'School results',
  'hs.navActivity': 'Activity',
  'hs.title': 'Head of school',
  'hs.sections': 'Head of school sections',

  // ── who you are here ──
  'hs.youArePrimary': 'You’re the primary head of school: you decide approvals and manage your deputies.',
  'hs.youAreDeputy': (primary: string) => `You’re a deputy head of school. The primary head is ${primary}.`,
  'hs.youAreDeputyNoPrimary': 'You’re a deputy head of school.',
  'hs.scopeWhole': 'You see the whole school.',
  'hs.scopeGrades': (g: string) => `You see grades ${g} only.`,
  'hs.delegateYou': 'You’re the approval delegate: you can decide requests while the primary head is away.',
  'hs.delegateYouUntil': (d: string) =>
    `You’re the approval delegate until ${d}: you can decide requests while the primary head is away.`,

  // ── approvals ──
  'hs.approvalsHintDelegate':
    'As approval delegate you decide requests that widen what someone can see — never one you asked for or one about you. The primary head is told what you decide. Requests expire after 14 days.',
  'hs.approvalsNone': 'Nothing is waiting for your approval.',
  'hs.before': 'Now',
  'hs.after': 'If approved',
  'hs.noRolesYet': 'No role yet',
  'hs.plusRole': (r: string) => `adds ${r}`,
  'hs.wholeSchool': 'whole school',
  'hs.gradesList': (g: string) => `grades ${g}`,
  'hs.primaryMark': 'primary',
  'hs.cannotOwn': 'You can’t decide this one: you asked for it, or it’s about you. The primary head decides it.',
  'hs.cannotOwnPrimary': 'You can’t decide a request about yourself. Your approval delegate can, or it expires.',
  'hs.daysLeft': (d: number) => (d <= 0 ? 'expires today' : `${d} ${n(d, 'day', 'days')} left`),

  // ── deputies (primary only) ──
  'hs.deputiesTitle': 'Deputy heads',
  'hs.deputiesIntro': 'Choose what each deputy sees, who decides approvals while you’re away, and hand over the primary role.',
  'hs.deputiesNone': 'No deputy heads yet. Your school admin adds them; you approve each one here.',
  'hs.scopeWholeShort': 'Sees the whole school',
  'hs.scopeGradesShort': (g: string) => `Sees grades ${g}`,
  'hs.delegatePill': 'Approval delegate',
  'hs.delegatePillUntil': (d: string) => `Approval delegate until ${d}`,
  'hs.changeScope': 'Change what they see',
  'hs.makeDelegate': 'Make approval delegate',
  'hs.makePrimary': 'Make primary',
  'hs.scopeTitle': (name: string) => `What ${name} sees`,
  'hs.scopeWhy': 'Only you can set this, because it decides what wellbeing data a person sees. Narrowing applies at once. They’re notified either way.',
  'hs.scopeWholeOption': 'Whole school',
  'hs.scopeWholeHint': 'School-wide and every grade’s results.',
  'hs.scopeGradesOption': 'Assigned grades only',
  'hs.scopeGradesHint': 'Only the grades you tick. They don’t see the school-wide total, so hidden grades can’t be worked out from it.',
  'hs.scopeGradesLegend': 'Grades',
  'hs.scopePickOne': 'Tick at least one grade.',
  'hs.scopeNoGrades': 'This school has no grades for this year yet, so only “Whole school” is possible.',
  'hs.scopeSaved': (name: string) => `Saved. ${name} has been notified.`,
  'hs.gradeN': (g: string) => `Grade ${g}`,

  // ── approval delegate ──
  'hs.delegateTitle': (name: string) => `Make ${name} approval delegate`,
  'hs.delegateIntro':
    'While it lasts, requests that widen access go to both of you. Whoever acts first decides, and the other is told. A delegate never decides a request they made or one about themselves. Every decision is recorded.',
  'hs.delegateUntil': 'Until (optional)',
  'hs.delegateUntilHint': 'The last day it applies. Leave empty to keep it until you end it. Up to a year ahead.',
  'hs.delegateReplaces': (name: string) => `This replaces ${name} as delegate.`,
  'hs.delegateSaved': (name: string) => `${name} is now your approval delegate.`,
  'hs.delegateTitleBox': 'Approval delegate',
  'hs.delegateCurrent': (name: string) => `${name} can decide approvals for you.`,
  'hs.delegateCurrentUntil': (name: string, d: string) => `${name} can decide approvals for you until ${d}.`,
  'hs.delegateNone': 'No approval delegate. Requests wait for you, and expire after 14 days.',
  'hs.endDelegate': 'End delegation',
  'hs.endDelegateTitle': 'End the delegation?',
  'hs.endDelegateBody': (name: string) => `${name} will no longer be able to decide approvals. They’re told.`,
  'hs.delegateEnded': 'Delegation ended.',

  // ── hand-over ──
  'hs.handOverTitle': (name: string) => `Make ${name} the primary head?`,
  'hs.handOverBody':
    'They’ll decide approvals and manage deputies from now on. You stay on as a deputy head who sees the whole school. Any approval delegation ends. The change is kept in the school’s critical records.',
  'hs.handOverNote': 'Note (optional)',
  'hs.handOverNoteHint': 'For example why, or from when. Shown in the school’s Activity log.',
  'hs.handedOver': (name: string) => `${name} is now the primary head of school.`,

  // ── school status ──
  'hs.statusTitle': 'School status',
  'hs.statusIntro': (admins: string) => `Read-only. To change anything here, ask your school admin (${admins}).`,
  'hs.statusIntroNoAdmin': 'Read-only. Your school has no active school admin — contact Zencera support.',
  'hs.code': 'School code',
  'hs.type': 'Type',
  'hs.state': 'State',
  'hs.paperwork': 'Paperwork',
  'hs.missingSizes': (c: number) =>
    `${c} ${n(c, 'class has', 'classes have')} no expected size. Ask your school admin to add it — participation rates need it.`,

  // ── results tab ──
  'hs.resultsScoped':
    'You see assigned grades only, so the school-wide total isn’t shown to you — otherwise the grades hidden from you could be worked out from it.',
  'hs.resultsNext':
    'Grade-level results, weekly trends and participation by grade arrive with the teacher check-in update. Heads never see single classes.',

  // ── activity ──
  'hs.activityTitle': 'Activity',
  'hs.activityIntro':
    'Everything done in your school, newest first — including who viewed results (never the numbers they saw) and anything Zencera did in support mode.',

  // ── two-factor for school roles ──
  'mfaS.manageIntro': 'Your role sees school data, so you sign in with a code from an authenticator app too.',
  'mfaS.requiredNotice': 'Two-factor sign-in is required for your role in this school. Set up an authenticator to continue, or sign out.',
  'mfaS.lostDevice': 'Lost your phone? Use your backup authenticator. If you have none, Zencera support can reset it after confirming who you are.',
  'mfaS.back': 'Back',
  'mfaS.continue': 'Done — continue',

  // ── notifications ──
  'notif.approvalWaitingDelegate': (r: string) => `As approval delegate: a request to add ${r} is waiting.`,
  'notif.approvalExpired': (r: string) => `A request to add ${r} expired after 14 days without a decision.`,
  'notif.delegateApproved': (r: string) => `Your approval delegate approved adding ${r}.`,
  'notif.delegateRejected': (r: string) => `Your approval delegate rejected adding ${r}.`,
  'notif.primaryApproved': (r: string) => `The primary head approved adding ${r}.`,
  'notif.primaryRejected': (r: string) => `The primary head rejected adding ${r}.`,
  'notif.delegateSet': 'You’re now the approval delegate: you can decide requests while the primary head is away.',
  'notif.delegateSetUntil': (d: string) => `You’re the approval delegate until ${d}.`,
  'notif.delegateEnded': 'You’re no longer the approval delegate.',
  'notif.scopeWhole': 'You now see the whole school.',
  'notif.scopeGrades': (g: string) => `You now see grades ${g} only.`,
  'notif.primaryChanged': 'Your school’s primary head of school has changed.',

  // ── errors raised as swipe:<code> ──
  'err.primary_only': 'Only the primary head of school can do this.',
  'err.not_a_deputy': 'Choose an active deputy head of this school.',
  'err.bad_end_date': 'Choose an end date from today up to a year ahead.',
  'err.no_delegate': 'There is no approval delegate to end.',
  'err.note_too_long': 'Keep the note under 500 characters.',
  'err.size_required': 'Enter the expected number of students for each class.',

  // ── Zencera appoints a primary head on the school's instruction (0016) ──
  'ap.action': 'Appoint as primary head…',
  'ap.actionHint': 'Only when the school asks in writing — for example, the primary head has left and there’s no deputy. A second platform admin confirms.',
  'ap.title': (n: string) => `Appoint ${n} as primary head`,
  'ap.intro':
    'Use this only on the school signatory’s written instruction. A second platform admin must confirm it. Then this person becomes the primary head (with the head of school role if they don’t have it), and the current primary stays on as a deputy until you deactivate them. Logged in the school’s Activity and kept in the archive.',
  'ap.from': 'Instruction from (name and role at the school)',
  'ap.on': 'Date of the instruction',
  'ap.note': 'Note (optional)',
  'ap.requested': 'Requested. It now waits for a second platform admin to confirm it on Approvals.',
  'ap.what': (n: string) => `${n} — appoint as primary head`,
  'ap.instruction': (from: string, d: string) => `Instruction from ${from}, ${d}`,
  'ap.replaces': (n: string) => `Current primary: ${n} (stays on as a deputy)`,
  'ap.noPrimary': 'The school has no active primary head.',
  'ap.confirm': 'Confirm appointment',
  'notif.appointSecond': 'A primary head appointment needs a second platform admin to confirm it.',
  'err.appointment_instruction': 'Record who at the school gave the written instruction.',
  'err.instruction_date': 'Record the date of the instruction (within the last 60 days).',
  'err.already_primary': 'This person is already the primary head of school.',
  'err.appointment_pending': 'An appointment for this school is already waiting for a second admin.',
};

export type HeadStrings = Shape<typeof headEn>;

export const headFi: HeadStrings = {
  'hs.navOverview': 'Yleiskuva',
  'hs.navResults': 'Koulun tulokset',
  'hs.navActivity': 'Tapahtumat',
  'hs.title': 'Rehtori',
  'hs.sections': 'Rehtorin osiot',

  'hs.youArePrimary': 'Olet koulun päärehtori: päätät hyväksynnöistä ja hallinnoit apulaisrehtoreita.',
  'hs.youAreDeputy': (primary: string) => `Olet apulaisrehtori. Päärehtori on ${primary}.`,
  'hs.youAreDeputyNoPrimary': 'Olet apulaisrehtori.',
  'hs.scopeWhole': 'Näet koko koulun.',
  'hs.scopeGrades': (g: string) => `Näet vain vuosiluokat ${g}.`,
  'hs.delegateYou': 'Olet hyväksyntöjen sijainen: voit päättää pyynnöistä päärehtorin poissa ollessa.',
  'hs.delegateYouUntil': (d: string) =>
    `Olet hyväksyntöjen sijainen ${d} asti: voit päättää pyynnöistä päärehtorin poissa ollessa.`,

  'hs.approvalsHintDelegate':
    'Sijaisena päätät pyynnöistä, jotka laajentavat jonkun näkemiä tietoja — et kuitenkaan omista pyynnöistäsi etkä sinua koskevista. Päärehtorille kerrotaan päätöksistäsi. Pyynnöt vanhenevat 14 päivässä.',
  'hs.approvalsNone': 'Mikään ei odota hyväksyntääsi.',
  'hs.before': 'Nyt',
  'hs.after': 'Jos hyväksytään',
  'hs.noRolesYet': 'Ei vielä roolia',
  'hs.plusRole': (r: string) => `lisää roolin ${r}`,
  'hs.wholeSchool': 'koko koulu',
  'hs.gradesList': (g: string) => `vuosiluokat ${g}`,
  'hs.primaryMark': 'pää',
  'hs.cannotOwn': 'Et voi päättää tästä: pyysit sitä itse tai se koskee sinua. Päärehtori päättää siitä.',
  'hs.cannotOwnPrimary': 'Et voi päättää itseäsi koskevasta pyynnöstä. Sijaisesi voi, tai pyyntö vanhenee.',
  'hs.daysLeft': (d: number) => (d <= 0 ? 'vanhenee tänään' : `${d} ${n(d, 'päivä', 'päivää')} jäljellä`),

  'hs.deputiesTitle': 'Apulaisrehtorit',
  'hs.deputiesIntro': 'Valitse, mitä kukin apulaisrehtori näkee, kuka päättää hyväksynnöistä poissa ollessasi, ja siirrä päärehtorin rooli.',
  'hs.deputiesNone': 'Apulaisrehtoreita ei vielä ole. Koulun ylläpitäjä lisää heidät; hyväksyt jokaisen täällä.',
  'hs.scopeWholeShort': 'Näkee koko koulun',
  'hs.scopeGradesShort': (g: string) => `Näkee vuosiluokat ${g}`,
  'hs.delegatePill': 'Hyväksyntöjen sijainen',
  'hs.delegatePillUntil': (d: string) => `Hyväksyntöjen sijainen ${d} asti`,
  'hs.changeScope': 'Muuta näkyvyyttä',
  'hs.makeDelegate': 'Nimeä hyväksyntöjen sijaiseksi',
  'hs.makePrimary': 'Tee päärehtoriksi',
  'hs.scopeTitle': (name: string) => `Mitä ${name} näkee`,
  'hs.scopeWhy': 'Vain sinä voit asettaa tämän, koska se ratkaisee, mitä hyvinvointitietoja henkilö näkee. Rajaus tulee voimaan heti. Henkilölle ilmoitetaan joka tapauksessa.',
  'hs.scopeWholeOption': 'Koko koulu',
  'hs.scopeWholeHint': 'Koko koulun ja jokaisen vuosiluokan tulokset.',
  'hs.scopeGradesOption': 'Vain valitut vuosiluokat',
  'hs.scopeGradesHint': 'Vain valitsemasi vuosiluokat. Koko koulun kokonaismäärää ei näytetä, jottei piilotettuja vuosiluokkia voi päätellä siitä.',
  'hs.scopeGradesLegend': 'Vuosiluokat',
  'hs.scopePickOne': 'Valitse vähintään yksi vuosiluokka.',
  'hs.scopeNoGrades': 'Koululla ei vielä ole tämän lukuvuoden vuosiluokkia, joten vain ”Koko koulu” on mahdollinen.',
  'hs.scopeSaved': (name: string) => `Tallennettu. Henkilölle ${name} on ilmoitettu.`,
  'hs.gradeN': (g: string) => `Vuosiluokka ${g}`,

  'hs.delegateTitle': (name: string) => `Nimeä ${name} hyväksyntöjen sijaiseksi`,
  'hs.delegateIntro':
    'Sijaisuuden ajan pääsyä laajentavat pyynnöt menevät teille molemmille. Ensin toimiva päättää, ja toiselle kerrotaan. Sijainen ei koskaan päätä omasta pyynnöstään eikä itseään koskevasta. Jokainen päätös kirjataan.',
  'hs.delegateUntil': 'Asti (valinnainen)',
  'hs.delegateUntilHint': 'Viimeinen voimassaolopäivä. Jätä tyhjäksi, jos haluat päättää sen itse. Enintään vuoden päähän.',
  'hs.delegateReplaces': (name: string) => `Tämä korvaa sijaisena henkilön ${name}.`,
  'hs.delegateSaved': (name: string) => `${name} on nyt hyväksyntöjen sijaisesi.`,
  'hs.delegateTitleBox': 'Hyväksyntöjen sijainen',
  'hs.delegateCurrent': (name: string) => `${name} voi päättää hyväksynnöistä puolestasi.`,
  'hs.delegateCurrentUntil': (name: string, d: string) => `${name} voi päättää hyväksynnöistä puolestasi ${d} asti.`,
  'hs.delegateNone': 'Ei hyväksyntöjen sijaista. Pyynnöt odottavat sinua ja vanhenevat 14 päivässä.',
  'hs.endDelegate': 'Päätä sijaisuus',
  'hs.endDelegateTitle': 'Päätetäänkö sijaisuus?',
  'hs.endDelegateBody': (name: string) => `${name} ei enää voi päättää hyväksynnöistä. Hänelle ilmoitetaan.`,
  'hs.delegateEnded': 'Sijaisuus päätetty.',

  'hs.handOverTitle': (name: string) => `Tehdäänkö henkilöstä ${name} päärehtori?`,
  'hs.handOverBody':
    'Hän päättää jatkossa hyväksynnöistä ja hallinnoi apulaisrehtoreita. Sinä jatkat apulaisrehtorina, joka näkee koko koulun. Mahdollinen hyväksyntöjen sijaisuus päättyy. Muutos säilytetään koulun tärkeissä asiakirjoissa.',
  'hs.handOverNote': 'Huomautus (valinnainen)',
  'hs.handOverNoteHint': 'Esimerkiksi syy tai mistä alkaen. Näkyy koulun tapahtumalokissa.',
  'hs.handedOver': (name: string) => `${name} on nyt koulun päärehtori.`,

  'hs.statusTitle': 'Koulun tila',
  'hs.statusIntro': (admins: string) => `Vain luku. Pyydä muutoksia koulun ylläpitäjältä (${admins}).`,
  'hs.statusIntroNoAdmin': 'Vain luku. Koulullasi ei ole aktiivista ylläpitäjää — ota yhteyttä Zenceran tukeen.',
  'hs.code': 'Koulun koodi',
  'hs.type': 'Tyyppi',
  'hs.state': 'Tila',
  'hs.paperwork': 'Sopimus',
  'hs.missingSizes': (c: number) =>
    `${c} ${n(c, 'luokalta', 'luokalta')} puuttuu oppilasmäärä. Pyydä koulun ylläpitäjää lisäämään se — osallistumisaste tarvitsee sen.`,

  'hs.resultsScoped':
    'Näet vain valitut vuosiluokat, joten koko koulun kokonaismäärää ei näytetä sinulle — muuten sinulta piilotetut vuosiluokat voisi päätellä siitä.',
  'hs.resultsNext':
    'Vuosiluokkakohtaiset tulokset, viikoittaiset trendit ja osallistuminen vuosiluokittain tulevat opettajien kirjautumispäivityksen myötä. Rehtorit eivät koskaan näe yksittäisiä luokkia.',

  'hs.activityTitle': 'Tapahtumat',
  'hs.activityIntro':
    'Kaikki koulussasi tehty, uusin ensin — myös kuka katsoi tuloksia (ei koskaan näkemiään lukuja) ja mitä Zencera teki tukitilassa.',

  'mfaS.manageIntro': 'Roolisi näkee koulun tietoja, joten kirjaudut myös todennussovelluksen koodilla.',
  'mfaS.requiredNotice': 'Kaksivaiheinen tunnistautuminen vaaditaan roolillesi tässä koulussa. Ota todennussovellus käyttöön jatkaaksesi, tai kirjaudu ulos.',
  'mfaS.lostDevice': 'Puhelin hukassa? Käytä varatodennussovellusta. Jos sinulla ei ole sellaista, Zenceran tuki voi nollata sen henkilöllisyytesi tarkistamisen jälkeen.',
  'mfaS.back': 'Takaisin',
  'mfaS.continue': 'Valmis — jatka',

  'notif.approvalWaitingDelegate': (r: string) => `Hyväksyntöjen sijaisena: pyyntö lisätä rooli ${r} odottaa.`,
  'notif.approvalExpired': (r: string) => `Pyyntö lisätä rooli ${r} vanheni 14 päivässä ilman päätöstä.`,
  'notif.delegateApproved': (r: string) => `Hyväksyntöjen sijaisesi hyväksyi roolin ${r} lisäämisen.`,
  'notif.delegateRejected': (r: string) => `Hyväksyntöjen sijaisesi hylkäsi roolin ${r} lisäämisen.`,
  'notif.primaryApproved': (r: string) => `Päärehtori hyväksyi roolin ${r} lisäämisen.`,
  'notif.primaryRejected': (r: string) => `Päärehtori hylkäsi roolin ${r} lisäämisen.`,
  'notif.delegateSet': 'Olet nyt hyväksyntöjen sijainen: voit päättää pyynnöistä päärehtorin poissa ollessa.',
  'notif.delegateSetUntil': (d: string) => `Olet hyväksyntöjen sijainen ${d} asti.`,
  'notif.delegateEnded': 'Et ole enää hyväksyntöjen sijainen.',
  'notif.scopeWhole': 'Näet nyt koko koulun.',
  'notif.scopeGrades': (g: string) => `Näet nyt vain vuosiluokat ${g}.`,
  'notif.primaryChanged': 'Koulusi päärehtori on vaihtunut.',

  'err.primary_only': 'Vain päärehtori voi tehdä tämän.',
  'err.not_a_deputy': 'Valitse tämän koulun aktiivinen apulaisrehtori.',
  'err.bad_end_date': 'Valitse päättymispäivä tästä päivästä enintään vuoden päähän.',
  'err.no_delegate': 'Päätettävää hyväksyntöjen sijaisuutta ei ole.',
  'err.note_too_long': 'Pidä huomautus alle 500 merkissä.',
  'err.size_required': 'Anna jokaiselle luokalle oppilasmäärä.',

  'ap.action': 'Nimitä päärehtoriksi…',
  'ap.actionHint': 'Vain koulun kirjallisesta pyynnöstä — esimerkiksi kun päärehtori on lähtenyt eikä apulaisrehtoria ole. Toinen alustan ylläpitäjä vahvistaa.',
  'ap.title': (n: string) => `Nimitä ${n} päärehtoriksi`,
  'ap.intro':
    'Käytä vain koulun allekirjoittajan kirjallisesta pyynnöstä. Toisen alustan ylläpitäjän on vahvistettava se. Sen jälkeen henkilöstä tulee päärehtori (rehtorin roolin kanssa, jos hänellä ei ole sitä), ja nykyinen päärehtori jatkaa apulaisrehtorina, kunnes poistat hänet käytöstä. Kirjataan koulun tapahtumiin ja arkistoon.',
  'ap.from': 'Pyynnön esittäjä (nimi ja rooli koulussa)',
  'ap.on': 'Pyynnön päivämäärä',
  'ap.note': 'Huomautus (valinnainen)',
  'ap.requested': 'Pyydetty. Se odottaa nyt toisen alustan ylläpitäjän vahvistusta Hyväksynnät-sivulla.',
  'ap.what': (n: string) => `${n} — nimitys päärehtoriksi`,
  'ap.instruction': (from: string, d: string) => `Pyynnön esitti ${from}, ${d}`,
  'ap.replaces': (n: string) => `Nykyinen päärehtori: ${n} (jatkaa apulaisrehtorina)`,
  'ap.noPrimary': 'Koululla ei ole aktiivista päärehtoria.',
  'ap.confirm': 'Vahvista nimitys',
  'notif.appointSecond': 'Päärehtorin nimitys odottaa toisen alustan ylläpitäjän vahvistusta.',
  'err.appointment_instruction': 'Kirjaa, kuka koulussa antoi kirjallisen pyynnön.',
  'err.instruction_date': 'Kirjaa pyynnön päivämäärä (enintään 60 päivän takaa).',
  'err.already_primary': 'Tämä henkilö on jo koulun päärehtori.',
  'err.appointment_pending': 'Tämän koulun nimitys odottaa jo toista ylläpitäjää.',
};

export const headSv: HeadStrings = {
  'hs.navOverview': 'Översikt',
  'hs.navResults': 'Skolans resultat',
  'hs.navActivity': 'Händelser',
  'hs.title': 'Rektor',
  'hs.sections': 'Rektorns avsnitt',

  'hs.youArePrimary': 'Du är skolans huvudrektor: du beslutar om godkännanden och hanterar biträdande rektorer.',
  'hs.youAreDeputy': (primary: string) => `Du är biträdande rektor. Huvudrektor är ${primary}.`,
  'hs.youAreDeputyNoPrimary': 'Du är biträdande rektor.',
  'hs.scopeWhole': 'Du ser hela skolan.',
  'hs.scopeGrades': (g: string) => `Du ser endast årskurserna ${g}.`,
  'hs.delegateYou': 'Du är ersättare för godkännanden: du kan besluta om begäranden när huvudrektorn är borta.',
  'hs.delegateYouUntil': (d: string) =>
    `Du är ersättare för godkännanden till och med ${d}: du kan besluta om begäranden när huvudrektorn är borta.`,

  'hs.approvalsHintDelegate':
    'Som ersättare beslutar du om begäranden som utökar vad någon ser — aldrig om en du själv gjort eller en som gäller dig. Huvudrektorn får veta dina beslut. Begäranden går ut efter 14 dagar.',
  'hs.approvalsNone': 'Inget väntar på ditt godkännande.',
  'hs.before': 'Nu',
  'hs.after': 'Om det godkänns',
  'hs.noRolesYet': 'Ingen roll ännu',
  'hs.plusRole': (r: string) => `får rollen ${r}`,
  'hs.wholeSchool': 'hela skolan',
  'hs.gradesList': (g: string) => `årskurserna ${g}`,
  'hs.primaryMark': 'huvud',
  'hs.cannotOwn': 'Du kan inte besluta om den här: du begärde den själv, eller den gäller dig. Huvudrektorn beslutar.',
  'hs.cannotOwnPrimary': 'Du kan inte besluta om en begäran som gäller dig själv. Din ersättare kan, annars går den ut.',
  'hs.daysLeft': (d: number) => (d <= 0 ? 'går ut i dag' : `${d} ${n(d, 'dag', 'dagar')} kvar`),

  'hs.deputiesTitle': 'Biträdande rektorer',
  'hs.deputiesIntro': 'Välj vad varje biträdande rektor ser, vem som beslutar om godkännanden när du är borta, och lämna över rollen som huvudrektor.',
  'hs.deputiesNone': 'Inga biträdande rektorer ännu. Skolans administratör lägger till dem; du godkänner var och en här.',
  'hs.scopeWholeShort': 'Ser hela skolan',
  'hs.scopeGradesShort': (g: string) => `Ser årskurserna ${g}`,
  'hs.delegatePill': 'Ersättare för godkännanden',
  'hs.delegatePillUntil': (d: string) => `Ersättare för godkännanden till ${d}`,
  'hs.changeScope': 'Ändra vad hen ser',
  'hs.makeDelegate': 'Gör till ersättare för godkännanden',
  'hs.makePrimary': 'Gör till huvudrektor',
  'hs.scopeTitle': (name: string) => `Vad ${name} ser`,
  'hs.scopeWhy': 'Bara du kan ställa in detta, eftersom det avgör vilka välbefinnandeuppgifter en person ser. En begränsning gäller genast. Personen meddelas i båda fallen.',
  'hs.scopeWholeOption': 'Hela skolan',
  'hs.scopeWholeHint': 'Hela skolans och varje årskurs resultat.',
  'hs.scopeGradesOption': 'Endast valda årskurser',
  'hs.scopeGradesHint': 'Endast de årskurser du kryssar i. Hen ser inte hela skolans total, så dolda årskurser kan inte räknas fram ur den.',
  'hs.scopeGradesLegend': 'Årskurser',
  'hs.scopePickOne': 'Kryssa i minst en årskurs.',
  'hs.scopeNoGrades': 'Skolan har inga årskurser för det här läsåret ännu, så bara ”Hela skolan” är möjligt.',
  'hs.scopeSaved': (name: string) => `Sparat. ${name} har meddelats.`,
  'hs.gradeN': (g: string) => `Årskurs ${g}`,

  'hs.delegateTitle': (name: string) => `Gör ${name} till ersättare för godkännanden`,
  'hs.delegateIntro':
    'Så länge det gäller går begäranden som utökar åtkomst till er båda. Den som agerar först beslutar, och den andra meddelas. En ersättare beslutar aldrig om en begäran hen själv gjort eller en som gäller hen. Varje beslut registreras.',
  'hs.delegateUntil': 'Till och med (valfritt)',
  'hs.delegateUntilHint': 'Sista dagen det gäller. Lämna tomt om du vill avsluta det själv. Högst ett år framåt.',
  'hs.delegateReplaces': (name: string) => `Detta ersätter ${name} som ersättare.`,
  'hs.delegateSaved': (name: string) => `${name} är nu din ersättare för godkännanden.`,
  'hs.delegateTitleBox': 'Ersättare för godkännanden',
  'hs.delegateCurrent': (name: string) => `${name} kan besluta om godkännanden i ditt ställe.`,
  'hs.delegateCurrentUntil': (name: string, d: string) => `${name} kan besluta om godkännanden i ditt ställe till och med ${d}.`,
  'hs.delegateNone': 'Ingen ersättare för godkännanden. Begäranden väntar på dig och går ut efter 14 dagar.',
  'hs.endDelegate': 'Avsluta ersättarskapet',
  'hs.endDelegateTitle': 'Avsluta ersättarskapet?',
  'hs.endDelegateBody': (name: string) => `${name} kan inte längre besluta om godkännanden. Hen meddelas.`,
  'hs.delegateEnded': 'Ersättarskapet avslutat.',

  'hs.handOverTitle': (name: string) => `Göra ${name} till huvudrektor?`,
  'hs.handOverBody':
    'Hen beslutar om godkännanden och hanterar biträdande rektorer från och med nu. Du fortsätter som biträdande rektor som ser hela skolan. Ett eventuellt ersättarskap upphör. Ändringen sparas i skolans viktiga handlingar.',
  'hs.handOverNote': 'Anteckning (valfritt)',
  'hs.handOverNoteHint': 'Till exempel varför eller från när. Visas i skolans händelselogg.',
  'hs.handedOver': (name: string) => `${name} är nu skolans huvudrektor.`,

  'hs.statusTitle': 'Skolans status',
  'hs.statusIntro': (admins: string) => `Endast läsning. Be skolans administratör (${admins}) om ändringar.`,
  'hs.statusIntroNoAdmin': 'Endast läsning. Din skola har ingen aktiv administratör — kontakta Zenceras support.',
  'hs.code': 'Skolkod',
  'hs.type': 'Typ',
  'hs.state': 'Status',
  'hs.paperwork': 'Avtal',
  'hs.missingSizes': (c: number) =>
    `${c} ${n(c, 'klass saknar', 'klasser saknar')} elevantal. Be skolans administratör lägga till det — deltagandet behöver det.`,

  'hs.resultsScoped':
    'Du ser endast valda årskurser, så hela skolans total visas inte för dig — annars kunde de årskurser som är dolda för dig räknas fram ur den.',
  'hs.resultsNext':
    'Resultat per årskurs, veckotrender och deltagande per årskurs kommer med uppdateringen för lärarnas incheckningar. Rektorer ser aldrig enskilda klasser.',

  'hs.activityTitle': 'Händelser',
  'hs.activityIntro':
    'Allt som gjorts i din skola, nyaste först — även vem som tittat på resultat (aldrig siffrorna hen såg) och vad Zencera gjort i supportläge.',

  'mfaS.manageIntro': 'Din roll ser skolans uppgifter, så du loggar också in med en kod från en autentiseringsapp.',
  'mfaS.requiredNotice': 'Tvåstegsinloggning krävs för din roll i den här skolan. Ställ in en autentiseringsapp för att fortsätta, eller logga ut.',
  'mfaS.lostDevice': 'Tappat telefonen? Använd din reservapp. Om du inte har någon kan Zenceras support återställa den efter att ha bekräftat vem du är.',
  'mfaS.back': 'Tillbaka',
  'mfaS.continue': 'Klart — fortsätt',

  'notif.approvalWaitingDelegate': (r: string) => `Som ersättare: en begäran om rollen ${r} väntar.`,
  'notif.approvalExpired': (r: string) => `En begäran om rollen ${r} gick ut efter 14 dagar utan beslut.`,
  'notif.delegateApproved': (r: string) => `Din ersättare godkände rollen ${r}.`,
  'notif.delegateRejected': (r: string) => `Din ersättare avslog rollen ${r}.`,
  'notif.primaryApproved': (r: string) => `Huvudrektorn godkände rollen ${r}.`,
  'notif.primaryRejected': (r: string) => `Huvudrektorn avslog rollen ${r}.`,
  'notif.delegateSet': 'Du är nu ersättare för godkännanden: du kan besluta om begäranden när huvudrektorn är borta.',
  'notif.delegateSetUntil': (d: string) => `Du är ersättare för godkännanden till och med ${d}.`,
  'notif.delegateEnded': 'Du är inte längre ersättare för godkännanden.',
  'notif.scopeWhole': 'Du ser nu hela skolan.',
  'notif.scopeGrades': (g: string) => `Du ser nu endast årskurserna ${g}.`,
  'notif.primaryChanged': 'Din skolas huvudrektor har bytts.',

  'err.primary_only': 'Bara huvudrektorn kan göra detta.',
  'err.not_a_deputy': 'Välj en aktiv biträdande rektor i den här skolan.',
  'err.bad_end_date': 'Välj ett slutdatum från i dag upp till ett år framåt.',
  'err.no_delegate': 'Det finns inget ersättarskap att avsluta.',
  'err.note_too_long': 'Håll anteckningen under 500 tecken.',
  'err.size_required': 'Ange elevantal för varje klass.',

  'ap.action': 'Utse till huvudrektor…',
  'ap.actionHint': 'Bara när skolan ber om det skriftligt — till exempel när huvudrektorn har slutat och det inte finns någon biträdande. En andra plattformsadministratör bekräftar.',
  'ap.title': (n: string) => `Utse ${n} till huvudrektor`,
  'ap.intro':
    'Använd bara på skolans undertecknares skriftliga begäran. En andra plattformsadministratör måste bekräfta. Därefter blir personen huvudrektor (med rektorsrollen om hen saknar den), och den nuvarande huvudrektorn fortsätter som biträdande tills du inaktiverar hen. Registreras i skolans händelser och i arkivet.',
  'ap.from': 'Begäran från (namn och roll i skolan)',
  'ap.on': 'Datum för begäran',
  'ap.note': 'Anteckning (valfritt)',
  'ap.requested': 'Begärt. Den väntar nu på att en andra plattformsadministratör bekräftar den under Godkännanden.',
  'ap.what': (n: string) => `${n} — utses till huvudrektor`,
  'ap.instruction': (from: string, d: string) => `Begäran från ${from}, ${d}`,
  'ap.replaces': (n: string) => `Nuvarande huvudrektor: ${n} (fortsätter som biträdande)`,
  'ap.noPrimary': 'Skolan har ingen aktiv huvudrektor.',
  'ap.confirm': 'Bekräfta utnämningen',
  'notif.appointSecond': 'En utnämning av huvudrektor väntar på en andra plattformsadministratörs bekräftelse.',
  'err.appointment_instruction': 'Ange vem i skolan som gav den skriftliga begäran.',
  'err.instruction_date': 'Ange datum för begäran (inom de senaste 60 dagarna).',
  'err.already_primary': 'Personen är redan skolans huvudrektor.',
  'err.appointment_pending': 'En utnämning för den här skolan väntar redan på en andra administratör.',
};
